import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, type DfeDefinitionSummary } from '@/lib/api';

export interface SelectedToken {
  itemDefId: string;
  name: string;
}

export function InventoryTokenPicker({
  label,
  selected,
  onSelect,
}: {
  label: string;
  selected: SelectedToken | null;
  onSelect: (token: SelectedToken | null) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DfeDefinitionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  const search = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.listDfeDefinitions({ search: q, fungibility: 'fungible', limit: 10 });
      setResults(res.definitions);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => search(query), 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  if (selected) {
    return (
      <div>
        <Label>{label}</Label>
        <div className="flex items-center gap-2 mt-1 min-w-0">
          <span className="text-sm font-medium truncate">{selected.name}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => {
              onSelect(null);
              setQuery('');
            }}
          >
            Change
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <Label>{label}</Label>
      <Input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => query.trim() && setOpen(true)}
        placeholder="Search by name..."
      />
      {open && (results.length > 0 || loading) && (
        <div className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md max-h-48 overflow-y-auto">
          {loading && <p className="px-3 py-2 text-sm text-muted-foreground">Searching...</p>}
          {results.map((def) => (
            <button
              key={def.itemDefId}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect({ itemDefId: def.itemDefId, name: def.name });
                setOpen(false);
              }}
            >
              {def.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
